import { ApolloClient, InMemoryCache, ApolloLink, split, fromPromise } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { createHttpLink } from '@apollo/client/link/http';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { fetchAuthSession } from 'aws-amplify/auth';

import awsConfig from '../aws-exports';

import { track } from 'core/helpers/analytics';

// We do this so that RetryLink will treat AppSync execution timeouts as errors
// and retry them. By default only network errors are considered errors for retrying,
// and errors within the GraphQL response are not considered "errors while making the request"
// and are not retried. This needs to be between the HTTP link and the retry link.
//
// Shape of data.errors:
// [
//   {
//     "data": null,
//     "errorInfo": null,
//     "errorType": "Lambda:ExecutionTimeoutException",
//     "locations": [{ "column": 3, "line": 2, "sourceName": null }],
//     "message": "Execution timed out.",
//     "path": ["recalculateCluster"]
//   }
// ]
const promoteAppSyncExecutionTimeoutsToErrorsLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((data) => {
    if (data && data.errors && data.errors.some((error) => error?.errorType?.includes('ExecutionTimeoutException'))) {
      // do not retry for certain lambdas (generateRenewal, runRenewalUW: see #29427 — both target the heavy RenewalsLambda)
      if (['rewritePolicy', 'generateRenewal', 'runRenewalUW'].includes(data.errors[0].path[0])) {
        return data;
      }
      throw new Error('The offer took too long to generate. Please try again or reach out to dev for support.');
    }
    return data;
  });
});

const unauthorizedAccessErrorLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (response.errors && response.errors.length > 0) {
      response.errors.forEach((err) => {
        if (err.errorType === 'AccessDeniedError') {
          const { type, reason, affinity, state } = err.errorInfo;
          const actionType = operation.operationName === 'getAccountPolicy' ? 'policy' : type;
          track('Agent not permitted to access record', {
            actionType,
            reason,
            affinity,
            state
          });
        }
      });
    }
    return response;
  });
});

const unauthorizedExceptionLink = onError(({ networkError, operation, forward }) => {
  if (networkError && networkError.statusCode === 401) {
    return fromPromise(
      fetchAuthSession({ forceRefresh: true })
        .then((session) => {
          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              // This sets fresh jwt in the context and makes apollo client retry the operation with the new token
              Authorization: session.tokens?.idToken?.toString()
            }
          }));
          return;
        })
        .catch(() => {
          // If we can't refresh, let the error propagate
          return;
        })
    ).flatMap(() => {
      return forward(operation);
    });
  }
});

const retryLink = new RetryLink({
  delay: {
    initial: 500,
    max: 3000
  },
  attempts: {
    max: 3,
    retryIf: (error) => {
      // Skip 401 errors as they're handled by unauthorizedExceptionLink
      return error?.response?.status !== 401;
    }
  }
});

const config = {
  url: awsConfig.graphqlEndpoint,
  region: awsConfig.region,
  auth: {
    type: awsConfig.authenticationType,
    jwtToken: async () => (await fetchAuthSession()).tokens?.idToken?.toString()
  }
};

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    unauthorizedAccessErrorLink,
    unauthorizedExceptionLink,
    retryLink,
    promoteAppSyncExecutionTimeoutsToErrorsLink,
    createAuthLink(config),
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      createSubscriptionHandshakeLink(config), // link for subscriptions
      createSubscriptionHandshakeLink(
        // link for graphql http requests
        awsConfig.graphqlEndpoint,
        createHttpLink({ uri: awsConfig.graphqlEndpoint, fetch: (...args) => window.fetch(...args) })
      )
    )
  ]),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache'
    },
    mutate: {
      fetchPolicy: 'no-cache'
    },
    watchQuery: {
      fetchPolicy: 'no-cache'
    }
  }
});
